import type { Pose } from "@soheil/core";
import { POSE_INDEX } from "@soheil/core";

/**
 * قرارداد Rive.
 *
 * این فایل تنها جایی است که «اپ» و «فایل طراح» با هم حرف می‌زنند. تا وقتی
 * طراح این چند اسم را رعایت کند، فایلش بدون هیچ تغییری در کد می‌نشیند —
 * و اگر فایلی نبود، اپ سراغ Lottie و بعد SVG می‌رود و کاربر چیزی نمی‌فهمد.
 *
 * سه لایه، به همین ترتیب:
 *   ۱. Rive   — اگر `/soheil/soheil.riv` وجود داشته باشد
 *   ۲. Lottie — انیمیشن‌های فعلی
 *   ۳. SVG    — رگ داخلی، همیشه هست
 */

export const RIVE_FILE = "soheil/soheil.riv";
export const ARTBOARD = "Soheil";
export const STATE_MACHINE = "Main";
/** ورودی عددی state machine: ۰ تا ۷، دقیقاً به ترتیب POSE_INDEX */
export const POSE_INPUT = "pose";

export const poseNumber = (pose: Pose): number => POSE_INDEX[pose];

type RiveInstance = {
  stateMachineInputs: (name: string) => Array<{ name: string; value: unknown }> | undefined;
  cleanup?: () => void;
  resizeDrawingSurfaceToCanvas?: () => void;
};

let available: Promise<boolean> | null = null;

/**
 * آیا فایل Rive روی سرور هست؟
 * یک‌بار می‌پرسیم و جواب را نگه می‌داریم؛ نبودش حالت عادی امروز است، نه خطا.
 */
export function riveAvailable(): Promise<boolean> {
  if (!available) {
    available = fetch(`${import.meta.env.BASE_URL}${RIVE_FILE}`, { method: "HEAD" })
      .then((r) => {
        if (!r.ok) return false;
        // سرورهای SPA برای مسیر ناموجود، index.html با کد ۲۰۰ می‌دهند.
        // بدون این بررسی، رانتایم Rive و چند مگابایت wasm بی‌دلیل بار می‌شود.
        const type = r.headers.get("content-type") ?? "";
        return !type.includes("text/html");
      })
      .catch(() => false);
  }
  return available;
}

export async function loadRive(canvas: HTMLCanvasElement, pose: Pose) {
  const mod = await import("@rive-app/canvas");
  const Rive = (mod as unknown as { Rive: new (o: Record<string, unknown>) => RiveInstance }).Rive;

  return new Promise<{ instance: RiveInstance; setPose: (p: Pose) => void }>((resolve, reject) => {
    let inst: RiveInstance;
    const timer = setTimeout(() => reject(new Error("rive load timeout")), 8000);

    const setPose = (p: Pose) => {
      const inputs = inst?.stateMachineInputs?.(STATE_MACHINE);
      const input = inputs?.find((i) => i.name === POSE_INPUT);
      if (input) input.value = poseNumber(p);
    };

    inst = new Rive({
      src: `${import.meta.env.BASE_URL}${RIVE_FILE}`,
      canvas,
      artboard: ARTBOARD,
      stateMachines: STATE_MACHINE,
      autoplay: true,
      onLoad: () => {
        clearTimeout(timer);
        inst.resizeDrawingSurfaceToCanvas?.();
        setPose(pose);
        resolve({ instance: inst, setPose });
      },
      onLoadError: (e: unknown) => {
        clearTimeout(timer);
        reject(e instanceof Error ? e : new Error("rive load failed"));
      },
    });
  });
}
